import React from 'react';
import CandleHop from './CandleHop';

// `compact` (mobile, Nina 08-03): the full pill ate a whole message-row of
// space above the composer. Compact = one slim top-docked line — dark scrim
// base (it floats over scene art), tighter type, name clamped so
// "Sonnet Five, Esq. (claude-sonnet-5)" can't wrap it to two lines.
// `candle` (the Claude House, Nina 09-24 "Between Rooms" D): the 🤔 and the
// bouncing dots give way to a little flame hopping along three dots. Same
// pill, same place, same timing; every other space passes nothing.
const TypingIndicator = ({ playerName, theme, compact = false, candle = false }) => {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: compact ? '7px' : '12px',
      padding: compact ? '5px 11px' : '12px 16px',
      backgroundColor: compact ? 'rgba(12,12,20,0.82)' : `${theme.accent}35`,
      backdropFilter: compact ? 'blur(4px)' : undefined,
      WebkitBackdropFilter: compact ? 'blur(4px)' : undefined,
      borderRadius: compact ? '999px' : '20px',
      marginBottom: compact ? 0 : '12px',
      animation: compact ? 'slideInDown 0.25s ease' : 'slideInUp 0.3s ease',
      border: `1px solid ${theme.accent}66`
    }}>
      {/* Animated thinking emoji */}
      {!candle && <span style={{
        fontSize: compact ? '1em' : '1.3em',
        animation: 'thinking 1.5s ease-in-out infinite'
      }}>
        🤔
      </span>}

      {/* Player name and status */}
      <span style={{
        color: '#ffffff',
        fontWeight: '600',
        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
        fontSize: compact ? '0.78em' : '0.95em',
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'baseline',
        gap: '4px',
      }}>
        <strong style={{
          color: '#ffffff',
          fontWeight: '700',
          ...(compact ? {
            maxWidth: '38vw', overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'bottom',
          } : {}),
        }}>{playerName}</strong> {candle ? 'is thinking' : 'is thinking...'}
      </span>

      {candle && <CandleHop style={{ fontSize: compact ? '0.95em' : '1.1em', marginLeft: compact ? 0 : '2px' }} />}

      {/* Animated dots */}
      {!candle && <div style={{
        display: 'flex',
        gap: compact ? '3px' : '4px',
        marginLeft: compact ? 0 : '4px'
      }}>
        {[0, 1, 2].map(i => (
          <div
            key={i}
            style={{
              width: compact ? '5px' : '8px',
              height: compact ? '5px' : '8px',
              borderRadius: '50%',
              backgroundColor: theme.accent,
              animation: `dotBounce 1.4s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`
            }}
          />
        ))}
      </div>}
    </div>
  );
};

// Add animations
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes slideInUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes slideInDown {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes thinking {
      0%, 100% {
        transform: rotate(0deg);
      }
      25% {
        transform: rotate(-10deg);
      }
      75% {
        transform: rotate(10deg);
      }
    }

    @keyframes dotBounce {
      0%, 60%, 100% {
        transform: translateY(0);
      }
      30% {
        transform: translateY(-6px);
      }
    }
  `;
  if (!document.head.querySelector('style[data-typing-indicator-styles]')) {
    styleSheet.setAttribute('data-typing-indicator-styles', 'true');
    document.head.appendChild(styleSheet);
  }
}

export default TypingIndicator;
